import { inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { injectIsBrowser } from '@app/helpers/is-browser';

import { AppRoutes } from '../../app-routes';

export enum MaintenanceStatus {
  preparing = 'maintenance-preparing',
  inProgress = 'maintenance-inprogress',
  finished = 'maintenance-finished',
}

export interface Maintenance {
  status: MaintenanceStatus;
  from?: Date;
  to?: Date;
}

const MAINTENANCE_LAST_STATE = 'maintenance_state';

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private _maintenance = new BehaviorSubject<Maintenance | null>(null);
  private router = inject(Router);
  private zone = inject(NgZone);
  private localStorage = inject(LocalStorageService);
  private isBrowser = injectIsBrowser();

  constructor() {
    this.initLastState();

    this._maintenance.subscribe((maintenanceState) => {
      this.localStorage.setItem(
        MAINTENANCE_LAST_STATE,
        JSON.stringify(maintenanceState),
      );
    });
  }

  public get maintenance() {
    return this._maintenance.value;
  }

  public get maintenance$() {
    return this._maintenance.asObservable();
  }

  public setMaintenance(status: MaintenanceStatus, from?: Date, to?: Date) {
    this._maintenance.next({
      status,
      from,
      to,
    });
  }

  public navigateServiceUnavailable() {
    if (!this.isBrowser) {
      return;
    }
    this.zone.run(() => {
      this.router.navigate([AppRoutes.serviceUnavailable]);
    });
  }

  public navigateMaintenance() {
    if (!this.isBrowser) {
      return;
    }
    this.zone.run(() => {
      this.router.navigate([AppRoutes.maintenance], {
        queryParams: { returnUrl: location.pathname },
      });
    });
  }

  public leaveMaintenanceMode(returnUrl?: string) {
    if (!returnUrl) {
      this.zone.run(() => {
        this.router.navigate(['/']);
      });
    } else {
      const routeCmd = returnUrl.split('/');
      routeCmd.shift();
      this.zone.run(() => {
        this.router.navigate(routeCmd);
      });
    }
  }

  private initLastState() {
    const lastStateValue = this.localStorage.getItem(MAINTENANCE_LAST_STATE);
    if (!lastStateValue) {
      return;
    }
    try {
      const lastState = JSON.parse(lastStateValue);
      this._maintenance.next({
        ...lastState,
        from: lastState?.from ? new Date(lastState.from) : undefined,
        to: lastState?.to ? new Date(lastState.to) : undefined,
      });
    } catch {
      return;
    }
  }
}
